import React, { useState } from 'react';
import { Menu, X, Home, Info, Star, BookOpen, Calendar } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Import the logo (adjust path as needed)
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { id: '/', label: 'Home', icon: <Home size={20} /> },
    { id: '/about', label: 'About Us', icon: <Info size={20} /> },
    { id: '/reviews', label: 'Testimonials', icon: <Star size={20} /> },
    { id: '/blogs', label: 'Blogs', icon: <BookOpen size={20} /> },
  ];

  const isActive = (path) => location.pathname === path;

  // Function to scroll to the event specialties section
  const scrollToEventSection = () => {
    setTimeout(() => {
      const element = document.getElementById('event-specialties');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle Book Event click
  const handleBookEvent = () => {
    if (location.pathname === '/') {
      scrollToEventSection();
    } else {
      navigate('/', { state: { scrollToEvent: true } });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed w-full z-50 bg-white/90 backdrop-blur-md shadow-lg shadow-yellow-100/50">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo - responsive sizing */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center justify-center">
              <img
                src={logo}
                alt="Jashn Logo"
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="flex items-center justify-center">
                      <div class="h-10 sm:h-12 md:h-14 lg:h-16 w-10 sm:w-12 md:w-14 lg:w-16 bg-gradient-to-r from-yellow-400 to-pink-400 flex items-center justify-center rounded-lg">
                        <span class="text-xl sm:text-2xl md:text-3xl font-bold text-white">J</span>
                      </div>
                    </div>
                  `;
                }}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
                Jashn
              </span>
              <span className="text-[0.6rem] sm:text-xs text-gray-500 font-medium -mt-1">
                Celebrations & Events
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.id}
                className={`flex items-center space-x-2 relative text-base lg:text-lg font-medium transition-all duration-300 hover:scale-105 ${
                  isActive(item.id)
                    ? 'text-yellow-600'
                    : 'text-gray-700 hover:text-yellow-500'
                }`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
                {isActive(item.id) && (
                  <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-full"></span>
                )}
              </Link>
            ))}
            <button
              onClick={handleBookEvent}
              className="flex items-center space-x-2 px-5 py-2 bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-yellow-200 transform hover:-translate-y-0.5 transition-all duration-300"
            >
              <Calendar size={18} />
              <span>Book Event</span>
            </button>
          </div>

          {/* Mobile menu button - always visible on right */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gray-700 hover:text-yellow-500 transition-colors flex-shrink-0 ml-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fadeIn border-t border-yellow-100 pt-4">
            <div className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.id}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center space-x-3 text-lg font-medium transition-all duration-300 ${
                    isActive(item.id)
                      ? 'text-yellow-600 bg-yellow-50 rounded-lg py-3 px-4'
                      : 'text-gray-700 hover:text-yellow-500 py-3 px-4'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
              <button
                onClick={handleBookEvent}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-yellow-200 transition-all duration-300 mx-4"
              >
                <Calendar size={18} />
                <span>Book Event</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;